/**
 * HostAnalyticsManager
 *
 * Central manager for all host-side analytics events.
 * Provides typed send*Event methods for every trackable event in the host flow,
 * ensuring consistent property shapes and a single auditable location for
 * all analytics calls.
 *
 * Events are grouped by the flow stage they represent:
 *  - App Lifecycle      (App Launched)
 *  - Network            (WebSocket connect/disconnect)
 *  - Auth               (User Authenticated, User Logged Out, MachineId Linked, Edition Set)
 *  - Guest Mode         (Host Entered Guest Mode)
 *  - Campaign           (Campaign Received/Cleared)
 *  - Matchmaking        (State Changed, Host Matched)
 *  - Game Lifecycle     (Game Launch Attempted, Game Launched, Game Closed)
 *  - Broadcast          (Broadcast Started/Ended — legacy IPC path)
 *  - Session Funnel     (Stream Started, Ready For Player, Peer Timeout, Session Started/Ended)
 *  - Player Management  (Host Added/Removed Player, Player Access Rights Updated)
 *  - Macro Execution    (Macro Executed)
 *
 * All send*Event methods are fire-and-forget — they never block the calling flow.
 * The sole exception is sendUserLoggedOutEvent, which callers must await so the
 * request is dispatched before the app reloads. See the method for details.
 */

import { sendAnalytics } from '@playcastdotio/Analytics';
import type {
  PlaycastAnalyticsSource,
  PlaycastAnalyticsAppLaunched,
  PlaycastAnalyticsEditionSet,
  PlaycastAnalyticsMachineIdLinked,
  PlaycastAnalyticsUserLoggedOut,
  PlaycastAnalyticsBroadcastStarted,
  PlaycastAnalyticsBroadcastEnded,
  PlaycastAnalyticsHostAddedPlayer,
  PlaycastAnalyticsHostRemovedPlayer,
  PlaycastAnalyticsPlayerAccessRightsUpdated,
  PlaycastAnalyticsMatchFound,
  PlaycastAnalyticsGameLaunched,
  PlaycastAnalyticsGameClosed,
  PlaycastAnalyticsHostEnteredGuestMode,
  PlaycastAnalyticsHostNetworkConnected,
  PlaycastAnalyticsHostNetworkDisconnected,
  PlaycastAnalyticsHostCampaignReceived,
  PlaycastAnalyticsHostCampaignCleared,
  PlaycastAnalyticsHostMatchmakingStateChanged,
  PlaycastAnalyticsHostStreamStarted,
  PlaycastAnalyticsHostReadyForPlayer,
  PlaycastAnalyticsHostPeerConnectionTimeout,
  PlaycastAnalyticsHostSessionStarted,
  PlaycastAnalyticsHostSessionEnded,
  PlaycastAnalyticsHostGameLaunchAttempted,
  PlaycastAnalyticsHostMacroExecuted,
} from '@playcastdotio/Analytics';
import type { IHostAnalyticsManager } from './IHostAnalyticsManager';
import { rewardsFlowMethods, type RewardsFlowMethods } from './events/rewardsFlowEvents';
import { sessionFlowMethods, type SessionFlowMethods } from './events/sessionFlowEvents';

/**
 * Upper bound on how long sendUserLoggedOutEvent waits before letting the logout
 * flow continue. The transport marks the request `keepalive`, so it survives the
 * unload once issued — this only has to cover the async work the transport does
 * *before* it calls fetch.
 *
 * That work is dominated by the transport's one-time keepalive support probe,
 * which is awaited ahead of the fetch and self-aborts after 1500ms. The budget
 * must therefore exceed that, or a cold probe would leave us reloading before
 * the request was ever issued. Once the probe settles the fetch is dispatched
 * synchronously, so there is no need to also cover the round trip.
 *
 * This is a cap, not a delay: the common path (probe already cached from earlier
 * events) resolves as soon as the request completes, typically well under 300ms.
 */
const LOGOUT_ANALYTICS_DISPATCH_MS = 2000;

export class HostAnalyticsManager implements IHostAnalyticsManager {

  // ========================================
  // Auth / App Setup
  // ========================================

  sendUserAuthenticatedEvent(source: PlaycastAnalyticsSource = 'electron'): void {
    sendAnalytics({
      name: 'User Authenticated',
      role: 'host',
      source,
      tier2Filters: ['machine'],
    });
  }

  /**
   * Logout counterpart to sendUserAuthenticatedEvent.
   *
   * Unlike every other method here this returns a promise, and callers must
   * await it: all logout paths reload the window immediately afterwards, which
   * would cancel a request that had not been issued yet.
   *
   * It never rejects — analytics must not be able to abort a logout.
   */
  async sendUserLoggedOutEvent(source: PlaycastAnalyticsSource = 'electron'): Promise<void> {
    const send = Promise.resolve(
      sendAnalytics({
        name: 'User Logged Out',
        role: 'host',
        source,
        tier2Filters: ['machine'],
      } as PlaycastAnalyticsUserLoggedOut),
    ).catch((error) => {
      console.error('[Analytics] Failed to send User Logged Out:', error);
    });

    let dispatchTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        send,
        new Promise<void>((resolve) => {
          dispatchTimer = setTimeout(resolve, LOGOUT_ANALYTICS_DISPATCH_MS);
        }),
      ]);
    } finally {
      if (dispatchTimer !== undefined) {
        clearTimeout(dispatchTimer);
      }
    }
  }

  sendMachineIdLinkedEvent(): void {
    sendAnalytics({
      name: 'MachineId Linked',
      role: 'host',
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsMachineIdLinked);
  }

  sendEditionSetEvent(edition: string): void {
    sendAnalytics({
      name: 'Edition Set',
      role: 'host',
      edition: edition as PlaycastAnalyticsEditionSet['edition'],
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsEditionSet);
  }

  // ========================================
  // P3 — Guest Mode
  // ========================================

  sendHostEnteredGuestModeEvent(machineId: string): void {
    sendAnalytics({
      name: 'Host Entered Guest Mode',
      role: 'host',
      machineId,
      tier2Filters: ['machine'],
      edition: 'pavilion',
    } as PlaycastAnalyticsHostEnteredGuestMode);
  }

  // ========================================
  // P4 — Network Connection
  // ========================================

  sendNetworkConnectedEvent(isReconnect: boolean): void {
    sendAnalytics({
      name: 'Host Network Connected',
      role: 'host',
      tier2Filters: ['machine'],
      isReconnect,
    } as PlaycastAnalyticsHostNetworkConnected);
  }

  sendNetworkDisconnectedEvent(machineId: string): void {
    sendAnalytics({
      name: 'Host Network Disconnected',
      role: 'host',
      machineId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsHostNetworkDisconnected);
  }

  // ========================================
  // Broadcast (legacy IPC streaming path)
  // ========================================

  sendBroadcastStartedEvent(params: { broadcastSessionId: string; invitationId: string }): void {
    sendAnalytics({
      name: 'Broadcast Started',
      role: 'host',
      broadcastSessionId: params.broadcastSessionId,
      invitationId: params.invitationId,
      source: 'electron',
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsBroadcastStarted);
  }

  sendBroadcastEndedEvent(params: { broadcastSessionId: string; invitationId: string }): void {
    sendAnalytics({
      name: 'Broadcast Ended',
      role: 'host',
      broadcastSessionId: params.broadcastSessionId,
      invitationId: params.invitationId,
      source: 'electron',
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsBroadcastEnded);
  }

  // ========================================
  // Player Management
  // ========================================

  sendHostAddedPlayerEvent(params: {
    connectedPlayerAuthId: string;
    connectedPersistentId: string;
    playerName: string;
    invitationId: string;
  }): void {
    sendAnalytics({
      name: 'Host Added Player',
      role: 'host',
      connectedPlayerAuthId: params.connectedPlayerAuthId,
      connectedPersistentId: params.connectedPersistentId,
      playerName: params.playerName,
      invitationId: params.invitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsHostAddedPlayer);
  }

  sendHostRemovedPlayerEvent(params: {
    connectedPlayerAuthId: string;
    connectedPersistentId: string;
    playerName: string;
    invitationId: string;
    closedBy: 'host' | 'player' | 'system';
  }): void {
    sendAnalytics({
      name: 'Host Removed Player',
      role: 'host',
      connectedPlayerAuthId: params.connectedPlayerAuthId,
      connectedPersistentId: params.connectedPersistentId,
      playerName: params.playerName,
      invitationId: params.invitationId,
      closedBy: params.closedBy,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsHostRemovedPlayer);
  }

  sendPlayerAccessRightsUpdatedEvent(params: {
    connectedPlayerAuthId: string;
    connectedPersistentId: string;
    playerName: string;
    invitationId?: string;
    access: {
      spectator: boolean;
      gamepad: boolean;
      keyboard: boolean;
      mouse: boolean;
      touch: boolean;
    };
  }): void {
    sendAnalytics({
      name: 'Player Access Rights Updated',
      role: 'host',
      connectedPlayerAuthId: params.connectedPlayerAuthId,
      connectedPersistentId: params.connectedPersistentId,
      playerName: params.playerName,
      invitationId: params.invitationId,
      access: params.access,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsPlayerAccessRightsUpdated);
  }

  // ========================================
  // P2 — Campaign
  // ========================================

  sendCampaignReceivedEvent(params: { campaignId: string; isFirstCampaign: boolean }): void {
    sendAnalytics({
      name: 'Host Campaign Received',
      role: 'host',
      tier2Filters: ['machine'],
      campaignId: params.campaignId,
      isFirstCampaign: params.isFirstCampaign,
    } as PlaycastAnalyticsHostCampaignReceived);
  }

  sendCampaignClearedEvent(params: { campaignId: string; reason?: string }): void {
    sendAnalytics({
      name: 'Host Campaign Cleared',
      role: 'host',
      tier2Filters: ['machine'],
      campaignId: params.campaignId,
      reason: params.reason,
    } as PlaycastAnalyticsHostCampaignCleared);
  }

  // ========================================
  // P2 — Matchmaking State
  // ========================================

  sendMatchmakingStateChangedEvent(params: {
    fromStatus: string;
    toStatus: string;
    campaignId?: string;
  }): void {
    sendAnalytics({
      name: 'Host Matchmaking State Changed',
      role: 'host',
      tier2Filters: ['machine'],
      fromStatus: params.fromStatus,
      toStatus: params.toStatus,
      campaignId: params.campaignId,
    } as PlaycastAnalyticsHostMatchmakingStateChanged);
  }

  // ========================================
  // P0 — Match Found
  // ========================================

  sendHostMatchedEvent(params: { matchId: string }): void {
    sendAnalytics({
      name: 'Host Matched',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.matchId,
      source: 'electron',
    } as PlaycastAnalyticsMatchFound);
  }

  // ========================================
  // P5 — Game Launch Attempt
  // ========================================

  sendGameLaunchAttemptedEvent(params: { campaignId: string; gameName: string }): void {
    sendAnalytics({
      name: 'Host Game Launch Attempted',
      role: 'host',
      tier2Filters: ['machine'],
      campaignId: params.campaignId,
      gameName: params.gameName,
    } as PlaycastAnalyticsHostGameLaunchAttempted);
  }

  // ========================================
  // P0 — Game Lifecycle
  // ========================================

  sendGameLaunchedEvent(params: { gameId: number; gameName: string }): void {
    sendAnalytics({
      name: 'Game Launched',
      role: 'host',
      tier2Filters: ['machine'],
      gameId: params.gameId,
      gameName: params.gameName,
      source: 'electron',
    } as PlaycastAnalyticsGameLaunched);
  }

  sendGameClosedEvent(params: { gameId: number; gameName: string }): void {
    sendAnalytics({
      name: 'Game Closed',
      role: 'host',
      tier2Filters: ['machine'],
      gameId: params.gameId,
      gameName: params.gameName,
      source: 'electron',
    } as PlaycastAnalyticsGameClosed);
  }

  // ========================================
  // P1 — Session Funnel
  // ========================================

  sendStreamStartedEvent(params: { invitationId?: string; campaignId?: string }): void {
    sendAnalytics({
      name: 'Host Stream Started',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.invitationId,
      campaignId: params.campaignId,
    } as PlaycastAnalyticsHostStreamStarted);
  }

  sendReadyForPlayerEvent(params: {
    invitationId?: string;
  }): void {
    sendAnalytics({
      name: 'Host Ready For Player',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.invitationId,
    } as PlaycastAnalyticsHostReadyForPlayer);
  }

  sendPeerConnectionTimeoutEvent(params: {
    invitationId?: string;
    campaignId?: string;
  }): void {
    sendAnalytics({
      name: 'Host Peer Connection Timeout',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.invitationId,
      campaignId: params.campaignId,
    } as PlaycastAnalyticsHostPeerConnectionTimeout);
  }

  sendSessionStartedEvent(params: {
    invitationId?: string;
    campaignId?: string;
  }): void {
    sendAnalytics({
      name: 'Host Session Started',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.invitationId,
      campaignId: params.campaignId,
    } as PlaycastAnalyticsHostSessionStarted);
  }

  sendSessionEndedEvent(params: {
    invitationId?: string;
    campaignId?: string;
    reason?: string;
    sessionDurationMs: number;
  }): void {
    sendAnalytics({
      name: 'Host Session Ended',
      role: 'host',
      tier2Filters: ['machine'],
      invitationId: params.invitationId,
      campaignId: params.campaignId,
      reason: params.reason,
      sessionDurationMs: params.sessionDurationMs,
    } as PlaycastAnalyticsHostSessionEnded);
  }

  // ========================================
  // P5 — Macro Execution
  // ========================================

  sendMacroExecutedEvent(params: {
    macroType: 'onLaunch' | 'onJoin' | 'onExit' | 'onInactive';
    success: boolean;
    campaignId: string;
  }): void {
    sendAnalytics({
      name: 'Host Macro Executed',
      role: 'host',
      tier2Filters: ['machine'],
      macroType: params.macroType,
      success: params.success,
      campaignId: params.campaignId,
    } as PlaycastAnalyticsHostMacroExecuted);
  }

  // ========================================
  // App Lifecycle
  // ========================================

  sendAppLaunchedEvent(): void {
    sendAnalytics({
      name: 'App Launched',
      role: 'host',
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsAppLaunched);
  }

}

// Mixin extracted event methods onto the prototype so the class satisfies
// IHostAnalyticsManager without duplicating 300+ lines of thin wrappers.
export interface HostAnalyticsManager extends RewardsFlowMethods, SessionFlowMethods {}
Object.assign(HostAnalyticsManager.prototype, rewardsFlowMethods, sessionFlowMethods);
